import { Box, TextField, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useState } from 'react';
import axiosService from '../../../utils/axios';
import { User, USER_TYPE_OPTIONS, USER_TYPE_CLIENT } from '../../../types/auth/types';
import { Choice } from '../../../types/vehicles/types';
import ModalPopup from '../../../components/Modal/ModalPopup';
import PasswordField from '../../../components/PasswordField/PasswordField';
import { PASSWORD_RULES } from '../../../utils/passwordRules';

interface Props {
  user?: User;
  userName?: string;
  onSave: (user: User) => void;
  modalTitle: string;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

interface RegistrationError {
  email: string;
  password: string;
}

export default function AddOrEditUser(props: Props) {
  const { user, userName = '', onSave, modalTitle, isModalOpen, setModalOpen } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RegistrationError | null>();

  const [firstNameInput, setFirstNameInput] = useState(user?.first_name || userName);
  const [lastNameInput, setLastNameInput] = useState(user?.last_name || '');
  const [phoneInput, setPhoneInput] = useState(user?.phone || '');
  const [addressInput, setAddressInput] = useState(user?.address || '');
  const [emailInput, setEmailInput] = useState(user?.email || '');
  const [passwordInput, setPasswordInput] = useState('');
  const [userTypeChoice, setUserTypeChoice] = useState<Choice>(
    USER_TYPE_OPTIONS.find(option => option.value === user?.user_type) ||
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      USER_TYPE_OPTIONS.find(option => option.value === USER_TYPE_CLIENT)!
  );

  const formId = 'add-or-edit-user';

  const handleSubmit = () => {
    setLoading(true);
    const data = {
      // eslint-disable-next-line camelcase
      first_name: firstNameInput,
      // eslint-disable-next-line camelcase
      last_name: lastNameInput,
      email: emailInput,
      phone: phoneInput,
      address: addressInput,
      // eslint-disable-next-line camelcase
      user_type: userTypeChoice.value,
    };
    if (user) {
      // Update existing user
      if (passwordInput) {
        // @ts-ignore
        data.password = passwordInput;
      }
      axiosService
        .patch(`/user/${user.id}/`, data)
        .then(userData => {
          setLoading(false);
          setError(null);
          onSave(userData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    } else {
      // Create new user
      axiosService
        .post('/auth/register/', { ...data, password: passwordInput })
        .then(userData => {
          setLoading(false);
          setError(null);
          onSave(userData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    }
  };

  return (
    <ModalPopup
      title={modalTitle}
      open={isModalOpen}
      onClose={() => setModalOpen(false)}
      actionButtons={
        <LoadingButton
          loading={loading}
          type="submit"
          form={formId}
          fullWidth
          variant="contained"
          disabled={!firstNameInput || !lastNameInput || !emailInput || (!user && !passwordInput)}
        >
          {user ? 'Edit user' : 'Create user'}
        </LoadingButton>
      }
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          component="form"
          id={formId}
          noValidate
          onSubmit={event => {
            event.preventDefault();
            handleSubmit();
          }}
          sx={{ width: '100%', mt: 1 }}
        >
          <Autocomplete
            fullWidth
            sx={{ mt: 2, mb: 1 }}
            value={userTypeChoice}
            onChange={(event, value) => setUserTypeChoice(value as Choice)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={option => option.display_name}
            options={USER_TYPE_OPTIONS}
            renderInput={params => (
              <TextField
                {...params}
                required
                label="User Type"
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
          />
          <TextField
            required
            fullWidth
            margin="normal"
            id="first-name"
            label="First name"
            autoFocus
            value={firstNameInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFirstNameInput(event.target.value);
            }}
          />
          <TextField
            required
            fullWidth
            margin="normal"
            id="last-name"
            label="Last name"
            value={lastNameInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setLastNameInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="phone"
            label="Phone"
            type="tel"
            value={phoneInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPhoneInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="address"
            label="Address"
            value={addressInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAddressInput(event.target.value);
            }}
          />
          <TextField
            required
            fullWidth
            margin="normal"
            id="email"
            label="Email"
            type="email"
            value={emailInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEmailInput(event.target.value);
            }}
            error={!!error?.email}
            helperText={error?.email ? error.email.toString() : ''}
          />
          <PasswordField
            margin="normal"
            required
            fullWidth
            label="Password"
            id="password"
            autoComplete="new-password"
            value={passwordInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPasswordInput(event.target.value);
            }}
            error={!!error?.password}
            helperText={error?.password ? error.password.toString() : ''}
            rules={PASSWORD_RULES}
          />
        </Box>
      </Box>
    </ModalPopup>
  );
}
